import { BuildingType, ThingType, WarriorType } from '../lib/types';

import { BUILDING_MODULES } from '../lib/constants';

import { getBuildingData, getTransaction, saveBuildingData, saveTransaction } from '../lib/dbCalls';

import { getThing } from './thingUtils';
import {
  addThing,
  addMoney,
  getWarriorThing,
  removeThing,
  getThingRepairCost,
  repairThing,
  itemCanBeBought,
  getThingUsedCost,
  updateImage,
  getDrassedRedThingsRepairCost,
  repairDressedRedThings,
  isThingSellable,
} from './warriorUtils';
import { sendAdminMsg } from '../components/Chat/utils';

import { getThingData as getShopThingData } from '../buildings/shop/utils';
import { getByValue } from './utils';

function grantAvatar(warrior: WarriorType, avatar: { id: string; image: string }): void {
  updateImage(warrior, avatar.image);
  if (!warrior.ownedAvatars) warrior.ownedAvatars = [];
  if (!warrior.ownedAvatars.includes(avatar.id)) warrior.ownedAvatars.push(avatar.id);
}

export function getBuilding(buildings: BuildingType[], id: string): BuildingType | undefined {
  // eslint-disable-next-line
  for (const item of buildings) {
    if (item.id === id) return item;
    if (item.children) {
      const result = getBuilding(item.children, id);
      if (result) return { ...result, parent: item } as any;
    }
  }

  return undefined;
}

export function getBuildingByCoords(
  buildings: BuildingType[],
  island: string,
  coordinateX: number,
  coordinateY: number,
): BuildingType | undefined {
  return buildings
    .filter((item) => item.island === island)
    .find((item) => {
      const { coordinates } = item;
      return (
        coordinates[0][0] <= coordinateX &&
        coordinates[0][1] <= coordinateY &&
        coordinates[1][0] >= coordinateX &&
        coordinates[1][1] >= coordinateY
      );
    });
}

export async function buildingApplyAction(
  id: string,
  actionName: string,
  params: any,
  warrior: WarriorType,
  initData: {
    buildings: BuildingType[];
    things: ThingType[];
  },
) {
  const { buildings, things } = initData;

  const building = getBuilding(buildings, id);
  if (!building) return false;

  switch (building.module) {
    case BUILDING_MODULES.SHOP.NAME: {
      switch (actionName) {
        case BUILDING_MODULES.SHOP.ACTIONS.BUY_CRYPTO:
        case BUILDING_MODULES.SHOP.ACTIONS.BUY: {
          const isCryptoBuy = actionName === BUILDING_MODULES.SHOP.ACTIONS.BUY_CRYPTO;

          const thing = getThing(things, params.thingId);
          if (!thing) return false;
          if (isCryptoBuy && !thing.priceCrypto) return false;

          let transaction;
          if (isCryptoBuy) {
            transaction = await getTransaction({
              transactionId: params.transactionId,
              warriorId: warrior.id,
              objectId: thing.id,
              amount: thing.priceCrypto,
            });
            if (!transaction) return false;
          }

          const buildingData = await getBuildingData(building.id);
          const thingData = getShopThingData(buildingData, thing.id);
          if (!itemCanBeBought(warrior, thing)) return false;
          if (thingData.count < 1) return false;
          addThing(warrior, params.thingId, things);
          thingData.count -= 1;
          if (!isCryptoBuy) addMoney(warrior, -thing.price);
          await saveBuildingData(buildingData);

          if (transaction) {
            await saveTransaction(transaction.id, {
              payed: true,
              solTransactionId: params.solTransactionId || '',
            });
          }

          if (isCryptoBuy) {
            sendAdminMsg({
              message: `Crypto buy: ${thing.name} by ${warrior.login}`,
            } as any);
          }
          break;
        }
        case BUILDING_MODULES.SHOP.ACTIONS.SELL: {
          const warriorThing = getWarriorThing(warrior, params.warriorThingId);
          if (!warriorThing || !isThingSellable(warriorThing)) return false;
          const thing = getThing(things, warriorThing.thing);
          addMoney(warrior, getThingUsedCost(warriorThing, thing));
          removeThing(warrior, params.warriorThingId);
          break;
        }
        case (BUILDING_MODULES.SHOP.ACTIONS as any).BUY_IAP: {
          const buildingData = await getBuildingData(building.id);
          const packItem = (buildingData.data.things as {
            id?: string;
            thing: string;
            iapProductId?: string;
          }[]).find((item) => item.id === params.packId || item.iapProductId === params.productId);
          if (!packItem || !packItem.iapProductId) return false;
          const thing = getThing(things, packItem.thing);
          if (!thing) return false;

          // Paid via Apple IAP — hand the warrior the scroll (same as BUY_CRYPTO;
          // no in-game money is deducted). The money scroll is applied later by
          // the player, not credited immediately.
          const thingData = getShopThingData(buildingData, thing.id);
          if (!itemCanBeBought(warrior, thing)) return false;
          if (thingData.count < 1) return false;
          addThing(warrior, thing.id, things);
          thingData.count -= 1;
          await saveBuildingData(buildingData);

          sendAdminMsg({
            message: `Hero ${warrior.login} ${warrior.id} bought ${thing.name} via Apple IAP`,
          } as any);
          break;
        }
        default:
      }
      break;
    }
    case BUILDING_MODULES.FORGE.NAME: {
      switch (actionName) {
        case BUILDING_MODULES.FORGE.ACTIONS.REPAIR: {
          if (!getWarriorThing(warrior, params.warriorThingId)) return false;
          const cost = getThingRepairCost(warrior, params.warriorThingId, params.count);
          if (cost > warrior.money) return false;
          repairThing(warrior, params.warriorThingId, params.count);
          addMoney(warrior, -Number(cost));
          break;
        }
        case (BUILDING_MODULES.FORGE.ACTIONS as any).REPAIR_ALL_RED: {
          const cost = getDrassedRedThingsRepairCost(warrior, things);
          if (cost > warrior.money) return false;
          repairDressedRedThings(warrior, things);
          addMoney(warrior, -Number(cost));
        }
        default:
      }
      break;
    }
    case BUILDING_MODULES.AVATAR_SHOP.NAME: {
      switch (actionName) {
        case BUILDING_MODULES.AVATAR_SHOP.ACTIONS.BUY: {
          const buildingData = await getBuildingData(building.id);
          const avatar = getByValue<{ id: string; name: string; price: number; image: string }>(
            buildingData.data.avatars,
            params.avatarId,
          );
          if (!avatar) return false;
          if (!itemCanBeBought(warrior, avatar)) return false;
          addMoney(warrior, -avatar.price);
          grantAvatar(warrior, avatar);
          sendAdminMsg({
            message: `Hero ${warrior.login} ${warrior.id} bought avatar ${avatar.name} for ${avatar.price} coins`,
          } as any);
          break;
        }
        case (BUILDING_MODULES.AVATAR_SHOP.ACTIONS as any).BUY_IAP: {
          const buildingData = await getBuildingData(building.id);
          const avatar = getByValue<{ id: string; name: string; price: number; image: string }>(
            buildingData.data.avatars,
            params.avatarId,
          );
          if (!avatar) return false;
          grantAvatar(warrior, avatar);
          sendAdminMsg({
            message: `Hero ${warrior.login} ${warrior.id} bought avatar ${avatar.name} via Apple IAP`,
          } as any);
          break;
        }
        case (BUILDING_MODULES.AVATAR_SHOP.ACTIONS as any).APPLY: {
          if (!warrior.ownedAvatars?.includes(params.avatarId)) return false;
          const buildingData = await getBuildingData(building.id);
          const avatar = getByValue<{ id: string; image: string }>(
            buildingData.data.avatars,
            params.avatarId,
          );
          if (!avatar) return false;
          updateImage(warrior, avatar.image);
          break;
        }
        default:
      }
      break;
    }
    case (BUILDING_MODULES as any).PORTAL.NAME: {
      switch (actionName) {
        case (BUILDING_MODULES as any).PORTAL.ACTIONS.TELEPORT: {
          const buildingData = await getBuildingData(building.id);
          const destinations = buildingData && buildingData.data && buildingData.data.destinations;
          if (!Array.isArray(destinations)) return false;
          const dest = destinations.find((d: any) => d.island === params.island);
          if (!dest) return false;
          if (!itemCanBeBought(warrior, dest)) return false;
          addMoney(warrior, -dest.price);
          const nextLocation: any = {
            island: dest.island,
            coordinateX: dest.coordinateX,
            coordinateY: dest.coordinateY,
          };
          if (dest.building) nextLocation.building = dest.building;
          warrior.location = nextLocation;
          break;
        }
        default:
      }
      break;
    }
    default:
  }

  return undefined;
}
